import { useMutation, useQuery } from '@apollo/client';
import { App } from 'antd';
import { FC } from 'react';
import {
  ApplicantContactDetailsContainerApplicantUserDocument,
  ApplicantContactDetailsContainerApplicantUserUpdateDocument,
  ApplicantUserPersonalInformationInput
} from '../../../../generated';
import { lookupCountryQuery } from './contact-details-country-query';
import { Country } from '../../../../types/country';
import { ComponentQueryLoader } from '../../../shared/component-query-loader';
import { useProfileSubmissionCheck } from '../../../shared/use-profile-submission-check';
import { ContactDetails } from './contact-details';

interface ContactDetailContainerProps {
  data: {
    applicantUserId: string;
  };
}
export interface GenerationalSuffix {
  suffix: string;
}

export interface SelectOption {
  label: string;
  value: string;
}

export const ContactDetailsContainer: FC<ContactDetailContainerProps> = (props) => {
  const { message } = App.useApp();

  const {
    data,
    error: contactDetailsError,
    loading
  } = useQuery(ApplicantContactDetailsContainerApplicantUserDocument, {
    variables: {
      applicantUserId: props.data.applicantUserId
    }
  });
  const { data: countryData, loading: countryLoading, error: countryError } = useQuery(lookupCountryQuery);

  useProfileSubmissionCheck(data, props.data.applicantUserId as string);

  const generationalSuffixOptions = [
    {
      suffix: 'Jr'
    },
    {
      suffix: 'Sr'
    },
    {
      suffix: 'I'
    },
    {
      suffix: 'II'
    },
    {
      suffix: 'III'
    },
    {
      suffix: 'IV'
    }
  ];

  const [applicantUserUpdate] = useMutation(ApplicantContactDetailsContainerApplicantUserUpdateDocument);

  const handleSave = async (values: ApplicantUserPersonalInformationInput) => {
    try {
      const result = await applicantUserUpdate({
        variables: {
          input: {
            id: props.data.applicantUserId,
            applicantUserDetails: {
              personalInformation: {
                ...values
              }
            }
          }
        }
      });
      if (result.data?.applicantUserUpdate.status.success) {
        message.success('Identity Details saved successfully');
      } else {
        console.log('Error saving contact details data:', result.data?.applicantUserUpdate.status.errorMessage);
        message.error(`Please fill all mandatory fields and ensure that your information is correct.`);
      }
    } catch (error) {
      message.error(`Error saving contact details: ${JSON.stringify(error)}`);
    }
  };

  const isLoading = loading || countryLoading;
  const error = contactDetailsError || countryError;
  const hasData = data && countryData?.countries?.eventPayload?.countries;

  return (
    <ComponentQueryLoader
      error={error}
      loading={isLoading}
      hasData={hasData}
      hasDataComponent={
        <ContactDetails
          data={{
            ...data?.applicantUser?.personalInformation,
            contactInformation: {
              ...data?.applicantUser?.personalInformation?.contactInformation,
              phone: data?.applicantUser?.personalInformation?.contactInformation?.phone?.map((phone) => {
                return {
                  id: phone?.id,
                  phoneNumber: phone?.phoneNumber,
                  phoneType: phone?.phoneType,
                  isPrimary: phone?.phoneNumber === data?.applicantUser?.personalInformation?.contactInformation?.primaryPhone
                };
              })
            }
          }}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          generationalSuffixOptions={generationalSuffixOptions}
          onSave={handleSave}
          applicantUserId={props.data.applicantUserId as string}
        />
      }
    />
  );
};

